import React, { useEffect, useMemo, useRef, useState } from 'react';

const drawerContent = {
  ro: {
    title: 'De ce ChessMindset?',
    subtitle:
      'Nu învățăm mutări mecanice. Construim o minte strategică ce funcționează sub presiune.',
    quote: 'Pe tablă nu câștigă cine știe mai multe mutări, ci cine gândește mai clar.',
    chapters: [
      {
        id: 'problem',
        label: 'Problema',
        headline: 'Jucătorii rămân blocați în automatism.',
        body:
          'Mulți jucători memorează deschideri, dar în poziții reale se pierd. Fără un model de gândire, fiecare mutare devine reactivă.',
        image:
          'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?auto=format&fit=crop&w=1800&q=80',
        alt: 'Tablă de șah dramatică, cu piese în tensiune'
      },
      {
        id: 'method',
        label: 'Metoda',
        headline: 'Structura pe niveluri: Începători, Intermediari, Performanță.',
        body:
          'Fiecare nivel combină tactică, strategie și mental game. Obiectivul nu este să reții variante, ci să înveți să iei decizii bune repetabil.',
        image:
          'https://images.unsplash.com/photo-1586165368502-1bad197a6461?auto=format&fit=crop&w=1800&q=80',
        alt: 'Piese de șah elegante, iluminate cinematic'
      },
      {
        id: 'result',
        label: 'Transformare',
        headline: 'Claritate în joc și încredere în competiție.',
        body:
          'Rezultatul este un stil de joc matur: evaluare corectă, plan concret și control emoțional. Exact mindset-ul care ridică nivelul pe termen lung.',
        image:
          'https://images.unsplash.com/photo-1611195974226-a6a9be9dd763?auto=format&fit=crop&w=1800&q=80',
        alt: 'Tablă de șah cu focus pe rege și regină'
      }
    ],
    cta: 'Începe cu nivelul tău în Academy'
  },
  en: {
    title: 'Why ChessMindset?',
    subtitle:
      'We do not teach mechanical moves. We build strategic thinking that works under pressure.',
    quote: 'On the board, the winner is not the player who memorized more moves, but the one who thinks clearly.',
    chapters: [
      {
        id: 'problem',
        label: 'Problem',
        headline: 'Most players get trapped in autopilot.',
        body:
          'Many players memorize openings, but lose clarity in real positions. Without a thinking model, every move becomes reactive.',
        image:
          'https://images.unsplash.com/photo-1528819622765-d6bcf132f793?auto=format&fit=crop&w=1800&q=80',
        alt: 'Dramatic chessboard with pieces under tension'
      },
      {
        id: 'method',
        label: 'Method',
        headline: 'Level-based structure: Beginner, Intermediate, Performance.',
        body:
          'Each level combines tactics, strategy, and mental game. The goal is not to memorize lines, but to make good decisions repeatedly.',
        image:
          'https://images.unsplash.com/photo-1586165368502-1bad197a6461?auto=format&fit=crop&w=1800&q=80',
        alt: 'Elegant chess pieces with cinematic lighting'
      },
      {
        id: 'result',
        label: 'Result',
        headline: 'Clarity in play and confidence in competition.',
        body:
          'The outcome is a mature game style: correct evaluation, concrete planning, and emotional control. That is long-term performance mindset.',
        image:
          'https://images.unsplash.com/photo-1611195974226-a6a9be9dd763?auto=format&fit=crop&w=1800&q=80',
        alt: 'Chessboard focused on king and queen'
      }
    ],
    cta: 'Start with your Academy level'
  }
};

function MissionDrawer({ language, isOpen, onClose, onOpenAcademy }) {
  const content = drawerContent[language] || drawerContent.en;
  const [activeChapterId, setActiveChapterId] = useState(content.chapters[0].id);
  const timerRef = useRef(null);

  useEffect(() => {
    setActiveChapterId(content.chapters[0].id);
  }, [content.chapters]);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  const activeChapterIndex = useMemo(
    () => content.chapters.findIndex((chapter) => chapter.id === activeChapterId),
    [content.chapters, activeChapterId]
  );
  const activeChapter = content.chapters[Math.max(activeChapterIndex, 0)];
  const progress = ((Math.max(activeChapterIndex, 0) + 1) / content.chapters.length) * 100;

  const computeDelayMs = (chapter) => {
    const raw = `${chapter?.headline || ''} ${chapter?.body || ''}`.trim();
    const len = raw.length;
    // Gentle loop: enough time to read each card.
    return Math.max(5200, Math.min(9500, 5200 + len * 14));
  };

  const goToIndex = (idx) => {
    const max = Math.max(0, content.chapters.length - 1);
    const clamped = Math.min(max, Math.max(0, idx));
    const next = content.chapters[clamped];
    if (next?.id) setActiveChapterId(next.id);
  };

  const goPrev = () => {
    const max = Math.max(0, content.chapters.length - 1);
    const nextIdx = activeChapterIndex <= 0 ? max : activeChapterIndex - 1;
    goToIndex(nextIdx);
  };

  const goNext = () => {
    const max = Math.max(0, content.chapters.length - 1);
    const nextIdx = activeChapterIndex >= max ? 0 : activeChapterIndex + 1;
    goToIndex(nextIdx);
  };

  // Auto-loop through chapters while the drawer is open.
  useEffect(() => {
    if (!isOpen) return undefined;
    if (prefersReducedMotion) return undefined;
    if (!content.chapters.length) return undefined;

    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      goNext();
    }, computeDelayMs(activeChapter));

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeChapterId, language]);

  return (
    <>
      <div
        className={`mission-overlay ${isOpen ? 'open' : ''}`}
        onClick={onClose}
      />
      <div className={`mission-drawer ${isOpen ? 'open' : ''}`}>
        <div className="drawer-handle"></div>
        <button className="drawer-close" onClick={onClose}>
          ✕
        </button>

        <div className={`drawer-hero drawer-hero-${activeChapter.id}`}>
          <img src={activeChapter.image} alt={activeChapter.alt} />
          <div className="drawer-hero-overlay">
            <h2 className="drawer-title">{content.title}</h2>
            <p className="drawer-subtitle">{content.subtitle}</p>
          </div>
        </div>

        <blockquote className="drawer-quote">{content.quote}</blockquote>

        <div className="drawer-progress">
          <div className="drawer-progress-track">
            <div className="drawer-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span>
            {Math.max(activeChapterIndex, 0) + 1}/{content.chapters.length}
          </span>
        </div>

        <div className="drawer-steps-wrap">
          <button
            type="button"
            className="drawer-arrow"
            onClick={goPrev}
            aria-label={language === 'ro' ? 'Înapoi' : 'Previous'}
          >
            ←
          </button>
          <div className="drawer-steps">
            {content.chapters.map((chapter, idx) => (
              <button
                key={chapter.id}
                className={`drawer-step ${chapter.id === activeChapter.id ? 'active' : ''}`}
                onClick={() => goToIndex(idx)}
              >
                {chapter.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="drawer-arrow"
            onClick={goNext}
            aria-label={language === 'ro' ? 'Înainte' : 'Next'}
          >
            →
          </button>
        </div>

        <div className="drawer-body">
          <h3 className="drawer-section-title">{activeChapter.headline}</h3>
          <p>{activeChapter.body}</p>
        </div>

        <div className="drawer-actions">
          <button className="btn-primary" onClick={onOpenAcademy}>
            {content.cta}
          </button>
        </div>
      </div>
    </>
  );
}

export default MissionDrawer;
